import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, X, AlertTriangle, CheckCircle2, Clock } from 'lucide-react';
import api from '../services/api';

const frecuenciaLabel = {
  DIARIO: 'Diario', SEMANAL: 'Semanal', QUINCENAL: 'Quincenal', MENSUAL: 'Mensual'
};

const estadoInfo = {
  EN_TIEMPO: { label: 'En tiempo', bg: '#f0fdf4', text: '#16a34a', icon: Clock },
  VENCIDO:   { label: 'Vencido', bg: '#fef2f2', text: '#dc2626', icon: AlertTriangle },
  FINALIZADO:{ label: 'Finalizado', bg: '#f1f5f9', text: '#475569', icon: CheckCircle2 },
};

const emptyForm = { nombre: '', descripcion: '', fechaInicio: '', fechaFin: '', frecuencia: 'SEMANAL', contratistaIds: [] };

export default function ProyectosExternosPage() {
  const navigate = useNavigate();
  const [proyectos, setProyectos] = useState([]);
  const [contratistas, setContratistas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  const fetchAll = async () => {
    const [proyRes, usersRes] = await Promise.all([
      api.get('/proyectos'),
      api.get('/auth/users')
    ]);
    setProyectos(proyRes.data);
    setContratistas(usersRes.data.filter(u => u.role === 'CONTRATISTA' && u.active));
  };

  useEffect(() => { fetchAll().finally(() => setLoading(false)); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post('/proyectos', form);
      setShowForm(false);
      setForm(emptyForm);
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al crear proyecto');
    } finally { setSubmitting(false); }
  };

  const toggleContratista = (id) => {
    setForm(f => ({
      ...f,
      contratistaIds: f.contratistaIds.includes(id)
        ? f.contratistaIds.filter(c => c !== id)
        : [...f.contratistaIds, id]
    }));
  };

  if (loading) return <p style={{ color: '#64748b' }}>Cargando...</p>;

  return (
    <div style={{ fontFamily: "'Segoe UI', sans-serif" }}>

      {/* Modal nuevo proyecto */}
      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: 16 }}>
          <div style={{ background: 'white', borderRadius: 20, padding: 24, width: '100%', maxWidth: 480, boxShadow: '0 8px 40px rgba(0,0,0,0.2)', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#1a2f6b' }}>Nuevo proyecto externo</h2>
              <button onClick={() => setShowForm(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}><X size={20} /></button>
            </div>
            <form onSubmit={handleCreate} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Nombre del proyecto</label>
                <input type="text" value={form.nombre} onChange={e => setForm({ ...form, nombre: e.target.value })} required
                  style={{ width: '100%', boxSizing: 'border-box', border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '10px 12px', fontSize: 14, fontFamily: 'inherit', outline: 'none' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Descripción (opcional)</label>
                <textarea value={form.descripcion} onChange={e => setForm({ ...form, descripcion: e.target.value })} rows={2}
                  style={{ width: '100%', boxSizing: 'border-box', border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '10px 12px', fontSize: 14, fontFamily: 'inherit', outline: 'none', resize: 'vertical' }} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Fecha inicio</label>
                  <input type="date" value={form.fechaInicio} onChange={e => setForm({ ...form, fechaInicio: e.target.value })} required
                    style={{ width: '100%', boxSizing: 'border-box', border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '10px 12px', fontSize: 14, fontFamily: 'inherit', outline: 'none' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Fecha fin estimada</label>
                  <input type="date" value={form.fechaFin} onChange={e => setForm({ ...form, fechaFin: e.target.value })} required
                    style={{ width: '100%', boxSizing: 'border-box', border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '10px 12px', fontSize: 14, fontFamily: 'inherit', outline: 'none' }} />
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Frecuencia de reporte</label>
                <select value={form.frecuencia} onChange={e => setForm({ ...form, frecuencia: e.target.value })}
                  style={{ width: '100%', border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '10px 12px', fontSize: 14, fontFamily: 'inherit', outline: 'none' }}>
                  {Object.entries(frecuenciaLabel).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Contratistas asignados</label>
                {contratistas.length === 0 ? (
                  <p style={{ fontSize: 12, color: '#94a3b8', margin: 0 }}>No hay contratistas registrados. Crea uno primero en "Usuarios y Áreas".</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 140, overflowY: 'auto', border: '1.5px solid #e2e8f0', borderRadius: 10, padding: 10 }}>
                    {contratistas.map(c => (
                      <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                        <input type="checkbox" checked={form.contratistaIds.includes(c.id)} onChange={() => toggleContratista(c.id)} />
                        {c.name} <span style={{ color: '#94a3b8' }}>({c.email})</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
                <button type="button" onClick={() => setShowForm(false)}
                  style={{ flex: 1, padding: '11px', border: '1.5px solid #e2e8f0', borderRadius: 10, background: 'white', fontSize: 14, cursor: 'pointer', fontFamily: 'inherit', color: '#64748b' }}>
                  Cancelar
                </button>
                <button type="submit" disabled={submitting}
                  style={{ flex: 1, padding: '11px', border: 'none', borderRadius: 10, background: '#2563eb', color: 'white', fontSize: 14, fontWeight: 700, cursor: submitting ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}>
                  {submitting ? 'Guardando...' : 'Crear proyecto'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: '#1a2f6b', margin: 0 }}>Proyectos Externos</h1>
        <button onClick={() => setShowForm(true)}
          style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#2563eb', color: 'white', border: 'none', borderRadius: 10, padding: '9px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
          <Plus size={15} /> Nuevo proyecto
        </button>
      </div>

      {proyectos.length === 0 && <p style={{ textAlign: 'center', color: '#94a3b8', padding: 24 }}>No hay proyectos externos</p>}

      {proyectos.map(p => {
        const info = estadoInfo[p.estado] || estadoInfo.EN_TIEMPO;
        const Icon = info.icon;
        return (
          <div key={p.id} onClick={() => navigate(`/contratistas/${p.id}`)}
            style={{ background: 'white', borderRadius: 16, border: `1.5px solid ${p.estado === 'VENCIDO' ? '#fecaca' : '#e2e8f0'}`, padding: 18, marginBottom: 12, cursor: 'pointer', boxShadow: p.estado === 'VENCIDO' ? '0 2px 12px rgba(220,38,38,0.15)' : '0 2px 8px rgba(0,0,0,0.04)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
              <div>
                <p style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 700, color: '#1e293b' }}>{p.nombre}</p>
                {p.descripcion && <p style={{ margin: 0, fontSize: 13, color: '#64748b' }}>{p.descripcion}</p>}
              </div>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4, background: info.bg, color: info.text, padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>
                <Icon size={12} /> {info.label}
              </span>
            </div>

            {/* Barra de avance */}
            <div style={{ background: '#f1f5f9', borderRadius: 20, height: 8, overflow: 'hidden', marginBottom: 8 }}>
              <div style={{
                width: `${p.porcentaje}%`, height: '100%',
                background: p.estado === 'VENCIDO' ? '#dc2626' : p.porcentaje > 80 ? '#ea580c' : '#2563eb',
                transition: 'width 0.3s'
              }} />
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#64748b', flexWrap: 'wrap', gap: 8 }}>
              <span>{p.porcentaje}% avance</span>
              <span>📅 {new Date(p.fechaInicio).toLocaleDateString('es-MX')} — {new Date(p.fechaFin).toLocaleDateString('es-MX')}</span>
              <span>🔄 {frecuenciaLabel[p.frecuencia]}</span>
              <span>👷 {p.contratistas.length} contratista(s)</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}